
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/ui/logo";
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsiveSpacing, useResponsiveNavigation } from "@/lib/mobile-utils";
import NavbarDesktopMenu from "./NavbarDesktopMenu";
import NavbarMobileMenu from "./NavbarMobileMenu";
import NavbarMobileToggle from "./NavbarMobileToggle";

const NavbarSection = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { safeArea } = useResponsiveSpacing();
  const { navbarHeight } = useResponsiveNavigation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isMenuOpen]);

  return (
    <motion.nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${safeArea} ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-brand-primary/10' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="mobile-container">
        <div className={`flex items-center justify-between ${navbarHeight}`}>
          {/* Logo with enhanced mobile optimization */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group touch-feedback-subtle focus-visible-enhanced rounded-lg p-2 -m-2"
            aria-label="YouCalm - Voltar para início"
          >
            <Logo 
              size={isMobile ? "sm" : "md"} 
              className="transition-transform group-hover:scale-105 group-active:scale-95" 
            />
          </Link>

          {/* Navigation */}
          <div className="flex items-center space-x-2">
            {!isMobile ? (
              <NavbarDesktopMenu menuItems={[]} />
            ) : (
              <NavbarMobileToggle 
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
              />
            )}
          </div>
        </div>

        {/* Mobile Menu with enhanced animations and accessibility */}
        {isMobile && (
          <NavbarMobileMenu 
            menuItems={[]}
            setIsMenuOpen={setIsMenuOpen}
            isMenuOpen={isMenuOpen}
          />
        )}
      </div>
    </motion.nav>
  );
};

export default NavbarSection;
